import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Modal, ModalVariant, Button, ButtonVariant } from '@patternfly/react-core';

interface PopupBlockerContextType {
  showModal: boolean;
  showPopupBlockerModal: (onAttemptPopup: () => Promise<boolean>, onRedirect: () => void) => void;
  hidePopupBlockerModal: () => void;
  attemptPopupCallback: (() => Promise<boolean>) | null;
  redirectCallback: (() => void) | null;
}

const PopupBlockerContext = createContext<PopupBlockerContextType | undefined>(undefined);

/**
 * Provider component for popup blocker modal state management.
 * Wraps the app to provide popup blocker notification functionality.
 */
export const PopupBlockerProvider: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
  const [showModal, setShowModal] = useState(false);
  const [attemptPopupCallback, setAttemptPopupCallback] = useState<(() => Promise<boolean>) | null>(null);
  const [redirectCallback, setRedirectCallback] = useState<(() => void) | null>(null);

  const showPopupBlockerModal = useCallback((onAttemptPopup: () => Promise<boolean>, onRedirect: () => void) => {
    setAttemptPopupCallback(() => onAttemptPopup);
    setRedirectCallback(() => onRedirect);
    setShowModal(true);
  }, []);

  const hidePopupBlockerModal = useCallback(() => {
    setShowModal(false);
    setTimeout(() => {
      setAttemptPopupCallback(null);
      setRedirectCallback(null);
    }, 300);
  }, []);

  const value = useMemo(
    () => ({
      showModal,
      showPopupBlockerModal,
      hidePopupBlockerModal,
      attemptPopupCallback,
      redirectCallback,
    }),
    [showModal, showPopupBlockerModal, hidePopupBlockerModal, attemptPopupCallback, redirectCallback]
  );

  return (
    <PopupBlockerContext.Provider value={value}>
      {children}
      <PopupBlockerModalInternal />
    </PopupBlockerContext.Provider>
  );
};

/**
 * Hook to access popup blocker context.
 */
export const usePopupBlockerContext = (): PopupBlockerContextType => {
  const context = useContext(PopupBlockerContext);
  if (context === undefined) {
    throw new Error('usePopupBlockerContext must be used within a PopupBlockerProvider');
  }
  return context;
};

/**
 * Internal modal component that reads from context.
 */
const PopupBlockerModalInternal: React.FunctionComponent = () => {
  const {
    showModal,
    hidePopupBlockerModal,
    attemptPopupCallback,
    redirectCallback
  } = usePopupBlockerContext();

  const [isAttempting, setIsAttempting] = useState(false);
  const [stillBlocked, setStillBlocked] = useState(false);

  const attemptPopup = async () => {
    setIsAttempting(true);
    setStillBlocked(false);
    const succeeded = (await attemptPopupCallback?.()) ?? false;
    setIsAttempting(false);
    if (succeeded) {
      hidePopupBlockerModal();
    } else {
      setStillBlocked(true);
    }
  };

  const handleContinueWithRedirect = () => {
    redirectCallback?.();
    hidePopupBlockerModal();
  };

  return (
    <Modal
      variant={ModalVariant.small}
      title="The auth session has expired"
      isOpen={showModal}
      onClose={hidePopupBlockerModal}
      aria-describedby="popup-blocker-modal-description"
    >
      <p id="popup-blocker-modal-description" style={{ margin: 0, color: 'var(--text-primary)' }}>
        The page tried to renew it automatically by opening a pop-up, but the browser blocked it.
      </p>
      <p style={{ margin: '0.5rem 0 0', color: 'var(--text-primary)' }}>
        To make this seamless and avoid seeing this again, allow pop-ups for this site. Otherwise,
        trigger the renewal manually below, or refresh the page to renew it instead.
      </p>
      <p style={{ margin: '0.5rem 0 0', color: 'var(--text-primary)' }}>
        If this dialog is dismissed without renewing, some parts of the page may not work as
        expected.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1.5rem' }}>
        <Button variant={ButtonVariant.primary} isBlock isLoading={isAttempting} onClick={attemptPopup}>
          Trigger session renewal with pop-up
        </Button>
        {stillBlocked && (
          <p style={{ margin: 0, color: 'var(--status-warning-text)' }}>
            Still blocked. Allow pop-ups for this site, then try again.
          </p>
        )}
        <div style={{ textAlign: 'center' }}>
          <Button variant={ButtonVariant.link} onClick={handleContinueWithRedirect}>
            Trigger session renewal refreshing the page
          </Button>
        </div>
      </div>
    </Modal>
  );
};
